import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../services/axiosConfig";
import {
  CreateProducaoDto,
  Documento,
  HistoricoProducaoItem,
  Producao,
} from "../types/producao";
import { extractDateInput } from "../utils/date";
import { isAdminUser, isVerifiedUser } from "../utils/auth";
import { normalizeTag } from "../utils/tag";

type ApiListResponse<T> = {
  data: T[];
};

const toDateInput = (value?: string | null) => {
  if (!value) return "";
  return extractDateInput(value);
};

const parseAnexos = (valor?: string | null) => {
  const texto = String(valor ?? "").trim();
  if (!texto) return [] as string[];

  try {
    const parsed = JSON.parse(texto);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => String(item).trim()).filter(Boolean);
    }
  } catch {
    // Compatibilidade com registros antigos em texto
  }

  return texto
    .split(/\r?\n/)
    .map((linha) => linha.replace(/^-+\s*/, "").trim())
    .filter(Boolean);
};

const formatarCodigoDocumento = (valor?: string | null) => {
  const anexos = parseAnexos(valor);
  if (anexos.length === 0) return "";
  return anexos.join(", ");
};

const buildDocumentos = (producao: any): Documento[] => {
  const documentos: Documento[] = [];

  const listaPecas = formatarCodigoDocumento(producao.listaPecas);
  if (listaPecas) {
    documentos.push({
      id: `${producao.id}-listaPecas`,
      nome: "Lista de Peças",
      codigo: listaPecas,
    });
  }
  const sequencialMontagem =
    producao.sequenciaMontagem ?? producao.sequencialMontagem;
  const sequencialMontagemCodigo = formatarCodigoDocumento(sequencialMontagem);
  if (sequencialMontagemCodigo) {
    documentos.push({
      id: `${producao.id}-sequencialMontagem`,
      nome: "Sequencial de Montagem",
      codigo: sequencialMontagemCodigo,
    });
  }
  const inspecaoMontagem = formatarCodigoDocumento(producao.inspecaoMontagem);
  if (inspecaoMontagem) {
    documentos.push({
      id: `${producao.id}-inspecaoMontagem`,
      nome: "Inspeção de Montagem",
      codigo: inspecaoMontagem,
    });
  }
  const historicoEquipamento = formatarCodigoDocumento(
    producao.historicoEquipamento,
  );
  if (historicoEquipamento) {
    documentos.push({
      id: `${producao.id}-historicoEquipamento`,
      nome: "Histórico do Equipamento",
      codigo: historicoEquipamento,
    });
  }
  const procedimentoTestes =
    producao.procedimentoTesteInspecaoMontagem ?? producao.procedimentoTestes;
  const procedimentoTestesCodigo = formatarCodigoDocumento(procedimentoTestes);
  if (procedimentoTestesCodigo) {
    documentos.push({
      id: `${producao.id}-procedimentoTeste`,
      nome: "Procedimento para Testes",
      codigo: procedimentoTestesCodigo,
    });
  }

  return documentos;
};

const getDescricaoComplementar = (producao: any): string => {
  const descricao = producao.descricao ?? "";
  const tipoNome = producao.tipoEquipamento?.nome;

  if (!tipoNome || !descricao.startsWith(tipoNome)) {
    return descricao;
  }

  return descricao.slice(tipoNome.length).trim();
};

const getDescricaoItemSeriado = (item: {
  numero?: string;
  descricao?: string;
  numeroSerie?: string;
}) => {
  const descricao = item.descricao?.trim() ?? "";

  if (descricao.startsWith("Numero:") || descricao.includes(" - Serie:")) {
    return descricao;
  }

  const partes = [
    item.numero ? `Numero: ${item.numero.trim()}` : "",
    descricao,
    item.numeroSerie ? `Serie: ${item.numeroSerie.trim()}` : "",
  ].filter(Boolean);

  return partes.join(" - ");
};

const mapHistoricoProducao = (
  observacoes: any[] | undefined | null,
): HistoricoProducaoItem[] =>
  (observacoes ?? []).map((observacao: any) => ({
    id: observacao.id,
    descricao: String(observacao.descricao ?? "").trim(),
    responsavel: String(observacao.responsavel ?? "").trim(),
    criadoEm: observacao.criadoEm ?? undefined,
  }));

const getHistoricoNovo = (historico?: HistoricoProducaoItem[] | null) =>
  (historico ?? []).filter((item) => item.id.startsWith("novo-") || !item.id);

const parseItemSeriado = (
  item: { id: string; descricao?: string | null },
  index: number,
) => {
  const descricaoCompleta = item.descricao ?? "";
  const partes = descricaoCompleta.split(" - ");
  const numeroPart = partes.find((parte) => parte.startsWith("Numero: "));
  const seriePart = partes.find((parte) => parte.startsWith("Serie: "));
  const descricao = partes
    .filter(
      (parte) => !parte.startsWith("Numero: ") && !parte.startsWith("Serie: "),
    )
    .join(" - ");

  return {
    id: item.id,
    numero: numeroPart?.replace("Numero: ", "") || String(index + 1),
    descricao: descricao || descricaoCompleta,
    numeroSerie: seriePart?.replace("Serie: ", "") || "",
  };
};

export const mapApiToProducao = (producao: any): Producao => ({
  id: producao.id,
  numeroOrdem: String(producao.numeroOrdem ?? ""),
  numeroLote: producao.numeroLote ?? producao.loteProducao?.numeroLote ?? null,
  loteProducao: producao.loteProducao ?? undefined,
  numeroSerie: producao.numeroSerie ?? "",
  tag: normalizeTag(producao.tag),
  validade: toDateInput(producao.validade),
  dataSolicitacao: toDateInput(producao.dataSolicitacao),
  dataNecessidade: toDateInput(producao.dataNecessidade),
  dataInicio: toDateInput(producao.dataInicio),
  dataParalisacao: toDateInput(producao.dataParalisacao),
  dataPrevisao: toDateInput(producao.dataPrevisao ?? producao.previsaoTermino),
  dataTermino: toDateInput(producao.dataTermino),
  statusProducao: producao.statusProducao ?? "PROGRAMADA",
  tipoEquipamentoId: producao.tipoEquipamentoId ?? "",
  tipoEquipamentoNome: producao.tipoEquipamento?.nome ?? "",
  modelo: producao.modelo ?? "",
  descricao: getDescricaoComplementar(producao),
  itensSeriados: (producao.itensSeriados ?? []).map(parseItemSeriado),
  documentos: buildDocumentos(producao),
  observacoes: (producao.observacoes ?? [])
    .map((observacao: any) => observacao.descricao)
    .join("\n"),
  historicoProducao: mapHistoricoProducao(producao.observacoes),
  listaPecas: producao.listaPecas ?? "",
  sequencialMontagem:
    producao.sequenciaMontagem ?? producao.sequencialMontagem ?? "",
  inspecaoMontagem: producao.inspecaoMontagem ?? "",
  historicoEquipamento: producao.historicoEquipamento ?? "",
  procedimentoTestes:
    producao.procedimentoTesteInspecaoMontagem ??
    producao.procedimentoTestes ??
    "",
  createdAt: producao.criadoEm,
  updatedAt: producao.atualizadoEm,
  diasSolicitacao: producao.diasSolicitacao ?? null,
  diasProducao: producao.diasProducao ?? null,
  diasParalisacao: producao.diasParalisacao ?? null,
  situacaoPrazo: producao.situacaoPrazo ?? null,
  resultadoPrazo: producao.resultadoPrazo ?? null,
});

export const mapProducaoToApi = (producao: CreateProducaoDto | Producao) => ({
  dataSolicitacao: producao.dataSolicitacao || undefined,
  dataNecessidade: producao.dataNecessidade || undefined,
  dataInicio: producao.dataInicio || undefined,
  dataParalisacao: producao.dataParalisacao || undefined,
  previsaoTermino: producao.dataPrevisao || undefined,
  dataTermino: producao.dataTermino || undefined,
  validade: producao.validade || undefined,
  statusProducao: producao.statusProducao || undefined,
  tipoEquipamentoId: producao.tipoEquipamentoId || undefined,
  modelo: producao.modelo || undefined,
  descricaoComplemento: producao.descricao || undefined,
  listaPecas: producao.listaPecas || undefined,
  sequencialMontagem: producao.sequencialMontagem || undefined,
  inspecaoMontagem: producao.inspecaoMontagem || undefined,
  historicoEquipamento: producao.historicoEquipamento || undefined,
  procedimentoTestes: producao.procedimentoTestes || undefined,
  itensSeriados: producao.itensSeriados
    ?.map((item) => ({
      descricao: getDescricaoItemSeriado(item),
    }))
    .filter((item) => item.descricao),
});

const mapProducaoToLoteApi = (producao: CreateProducaoDto) => ({
  tipoEquipamentoId: producao.tipoEquipamentoId || undefined,
  modelo: producao.modelo,
  descricao: producao.descricao || undefined,
  solicitante: undefined,
  quantidade: Math.max(1, Number(producao.quantidade ?? 1)),
  dataSolicitacao: producao.dataSolicitacao || undefined,
  dataNecessidade: producao.dataNecessidade || undefined,
  dataInicio: producao.dataInicio || undefined,
  dataParalisacao: producao.dataParalisacao || undefined,
  previsaoTermino: producao.dataPrevisao || undefined,
  dataTermino: producao.dataTermino || undefined,
  validade: producao.validade || undefined,
  statusProducao: producao.statusProducao || undefined,
});

export const useProducoes = () => {
  const [producoes, setProducoes] = useState<Producao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carregarProducoes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get<ApiListResponse<any>>(
        "/producoes",
        {
          params: { limit: 100 },
        },
      );
      setProducoes(response.data.data.map(mapApiToProducao));
      setError(null);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Erro ao carregar produções",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarProducoes();
  }, [carregarProducoes]);

  const criarProducao = async (novaProducao: CreateProducaoDto) => {
    const response = await axiosInstance.post(
      "/lotes-producao",
      mapProducaoToLoteApi(novaProducao),
    );
    const primeiroEquipamento = response.data.equipamentos?.[0];
    let producaoCriada = primeiroEquipamento
      ? mapApiToProducao({
          ...primeiroEquipamento,
          loteProducao: response.data,
          numeroLote: response.data.numeroLote,
          modelo: response.data.modelo,
          descricao: response.data.descricao,
          dataSolicitacao: response.data.dataSolicitacao,
          dataNecessidade: response.data.dataNecessidade,
          dataInicio: response.data.dataInicio,
          dataParalisacao: response.data.dataParalisacao,
          previsaoTermino: response.data.previsaoTermino,
          dataTermino: response.data.dataTermino,
          statusProducao: response.data.statusProducao,
          tipoEquipamentoId: response.data.tipoEquipamentoId,
          tipoEquipamento: response.data.tipoEquipamento,
        })
      : mapApiToProducao(response.data);

    await carregarProducoes();
    return producaoCriada;
  };

  const atualizarProducao = async (
    id: string,
    producaoAtualizada: Producao,
  ) => {
    const response = await axiosInstance.put(
      `/producoes/${id}`,
      mapProducaoToApi(producaoAtualizada),
    );
    let producao = mapApiToProducao(response.data);

    const historicoNovo = getHistoricoNovo(
      producaoAtualizada.historicoProducao,
    );

    if (historicoNovo.length > 0) {
      await Promise.all(
        historicoNovo.map((item) =>
          axiosInstance.post(`/producoes/${id}/observacoes`, {
            descricao: item.descricao,
            responsavel: item.responsavel,
          }),
        ),
      );

      const refreshed = await axiosInstance.get(`/producoes/${id}`);
      producao = mapApiToProducao(refreshed.data);
    }

    const isMasterUser = isAdminUser() || isVerifiedUser();
    const tagNormalizada = normalizeTag(producaoAtualizada.tag);
    if (tagNormalizada && (producao.statusProducao === "CONCLUIDA" || isMasterUser) && tagNormalizada !== producao.tag) {
      const tagResponse = await axiosInstance.patch(`/producoes/${id}/tag`, {
        tag: tagNormalizada,
      });
      producao = mapApiToProducao(tagResponse.data);
    }

    await carregarProducoes();
    return producao;
  };

  return {
    producoes,
    loading,
    error,
    criarProducao,
    atualizarProducao,
  };
};
